import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, User, Search, Network, GitBranch, Layers, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

const SidePill = ({ side }) => {
  if (!side) return <span className="text-xs px-2 py-1 rounded bg-slate-700/40 text-slate-400">—</span>;
  const left = side === "left";
  return (
    <span className={`text-xs px-2 py-1 rounded capitalize font-semibold ${left ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"}`}>
      {side}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span className={`text-[10px] px-2 py-1 rounded-full ${active === false ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>
    {active === false ? "Inactive" : "Active"}
  </span>
);

// ---- recursive binary tree (up to 20 levels, collapsible) ----
const EmptySlot = ({ side }) => (
  <div className="w-32 py-4 px-3 rounded-xl bg-black/20 border border-dashed border-slate-700/70 text-center">
    <User className="w-4 h-4 mx-auto text-slate-700" />
    <div className={`text-[10px] mt-1 capitalize ${side === "L" ? "text-sky-500/60" : "text-amber-500/60"}`}>Open {side === "L" ? "left" : "right"}</div>
  </div>
);

const NodeCard = ({ node, side, canExpand, isOpen, onToggle, isRoot, isHighlight }) => {
  const ring = isHighlight
    ? "border-emerald-400 ring-4 ring-emerald-400/40 scale-105"
    : isRoot ? "border-amber-400 ring-2 ring-amber-500/30" : side === "L" ? "border-sky-500/50" : "border-amber-500/40";
  return (
    <div id={`tnode-${node.id}`} data-testid={`tree-node-${node.id}`} className={`relative w-40 p-3 rounded-2xl bg-[#12131A] border-2 ${ring} shadow-lg shadow-black/40 transition-all hover:-translate-y-0.5`}>
      <span className={`absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${node.active === false ? "bg-red-400" : "bg-emerald-400"}`} />
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${isRoot ? "gold-btn" : side === "L" ? "bg-sky-500/20" : "bg-amber-500/20"}`}>
          <User className={`w-4 h-4 ${isRoot ? "text-black" : side === "L" ? "text-sky-300" : "text-amber-300"}`} />
        </div>
        <div className="min-w-0 text-left">
          <div className="font-semibold text-xs truncate">{isRoot ? "You" : node.name}</div>
          <div className="font-mono-fin text-[10px] text-amber-400 truncate">@{node.username || node.referral_code}</div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">R{node.rank || 0}</span>
        <span className="font-mono-fin text-[10px] text-emerald-400">{money(node.business, 0)}</span>
      </div>
      {canExpand && (
        <button data-testid={`tree-toggle-${node.id}`} onClick={onToggle}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#1b1c24] border border-amber-500/40 grid place-items-center text-amber-300 hover:bg-amber-500/20 z-10">
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
};

function findPath(node, q) {
  if (!node) return null;
  const hit = [node.name, node.username, node.referral_code].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  if (hit) return [node.id];
  for (const k of ["left", "right"]) {
    const sub = findPath(node[k], q);
    if (sub) return [node.id, ...sub];
  }
  return null;
}

function renderNode(node, side, depth, expanded, toggle, highlightId) {
  if (!node) return <EmptySlot side={side} />;
  const hasChildren = !!(node.left || node.right);
  const canExpand = hasChildren && depth < 60;
  const isOpen = expanded.has(node.id);
  return (
    <div className="flex flex-col items-center">
      <NodeCard node={node} side={side} canExpand={canExpand} isOpen={isOpen} onToggle={() => toggle(node.id)} isRoot={depth === 0} isHighlight={node.id === highlightId} />
      {canExpand && isOpen && (
        <div className="flex flex-col items-center">
          <div className="w-px h-6 bg-amber-500/30 mt-3" />
          <div className="flex items-start gap-6 sm:gap-10 pt-2 relative">
            <div className="absolute top-0 left-[25%] right-[25%] h-px bg-amber-500/30" />
            {["left", "right"].map((k) => (
              <div key={k} className="flex flex-col items-center relative pt-4">
                <div className="absolute top-0 w-px h-4 bg-amber-500/30" />
                <span className={`text-[9px] uppercase tracking-widest mb-1.5 font-bold ${k === "left" ? "text-sky-400" : "text-amber-400"}`}>{k}</span>
                {renderNode(node[k], k === "left" ? "L" : "R", depth + 1, expanded, toggle, highlightId)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "direct", label: "Direct Referrals", icon: User },
  { id: "tree", label: "Binary Tree", icon: GitBranch },
  { id: "downline", label: "Full Downline", icon: Network },
  { id: "levels", label: "Level Count", icon: Layers },
];

export default function Team() {
  const [tree, setTree] = useState(null);
  const [dl, setDl] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("direct");
  const [expanded, setExpanded] = useState(new Set());
  const [treeQuery, setTreeQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);

  const searchTree = () => {
    const s = treeQuery.trim().toLowerCase();
    if (!s || !tree) return;
    const path = findPath(tree, s);
    if (!path) { setHighlightId(null); toast.error("No member found in your genealogy"); return; }
    setExpanded((prev) => { const n = new Set(prev); path.forEach((id) => n.add(id)); return n; });
    setHighlightId(path[path.length - 1]);
  };

  useEffect(() => {
    if (!highlightId) return;
    const el = document.getElementById(`tnode-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  }, [highlightId, expanded]);

  useEffect(() => {
    api.get("/me/team").then(({ data }) => {
      setTree(data.tree);
      // auto-expand root + first two levels
      const seed = new Set();
      const walk = (n, d) => {
        if (!n || d > 2) return;
        seed.add(n.id);
        walk(n.left, d + 1); walk(n.right, d + 1);
      };
      walk(data.tree, 0);
      setExpanded(seed);
    }).catch(() => toast.error("Load failed"));
    api.get("/me/downline").then(({ data }) => setDl(data)).catch(() => toast.error("Load failed"));
  }, []);

  const toggle = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const match = (m) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [m.name, m.username, m.referral_code, m.sponsor_name, m.email]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  };

  const directs = (dl?.directs || []).filter(match);
  const members = (dl?.members || []).filter(match);

  const levelEarnings = dl?.level_earnings || {};
  const levelCounts = (() => {
    const map = {};
    (dl?.members || []).forEach((m) => { map[m.level] = (map[m.level] || 0) + 1; });
    const maxLevel = Object.keys(map).length ? Math.max(...Object.keys(map).map(Number)) : 0;
    return Array.from({ length: maxLevel }, (_, i) => ({ level: i + 1, count: map[i + 1] || 0, earned: levelEarnings[String(i + 1)] || 0 }));
  })();
  const totalEarned = Object.values(levelEarnings).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-amber-400" /> My Team</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-right"><div className="text-[10px] text-slate-500 uppercase tracking-wide">Direct Team</div><div className="font-mono-fin text-lg font-bold text-amber-400">{dl?.directs_count ?? "—"}</div></div>
          <div className="text-right"><div className="text-[10px] text-slate-500 uppercase tracking-wide">Total Downline</div><div className="font-mono-fin text-lg font-bold text-emerald-400">{dl?.total ?? "—"}</div></div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="team-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`team-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? "gold-btn" : "glass text-slate-400 hover:text-amber-300"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {(tab === "direct" || tab === "downline") && (
        <div className="glass rounded-2xl p-4 flex items-center gap-3">
          <Search className="w-5 h-5 text-amber-400 shrink-0" />
          <input data-testid="team-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, username, code, or sponsor..."
            className="w-full bg-transparent outline-none text-sm placeholder:text-slate-500" />
        </div>
      )}

      {/* Binary Tree */}
      {tab === "tree" && tree && (
        <div className="glass rounded-2xl p-6" data-testid="binary-tree">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><GitBranch className="w-5 h-5 text-amber-400" /> Binary Genealogy Tree</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5 text-sky-400"><span className="w-2.5 h-2.5 rounded bg-sky-500/40 border border-sky-500" /> Left leg</span>
              <span className="flex items-center gap-1.5 text-amber-400"><span className="w-2.5 h-2.5 rounded bg-amber-500/40 border border-amber-500" /> Right leg</span>
              <span className="text-slate-500">Tap ⌄ to expand · up to 60 levels</span>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-6 max-w-md" data-testid="tree-search">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-amber-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="tree-search-input" value={treeQuery}
                onChange={(e) => setTreeQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchTree(); }}
                placeholder="Find a member in your tree by name, username or code..."
                className="w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none" />
            </div>
            <button data-testid="tree-search-btn" onClick={searchTree} className="gold-btn px-4 py-2.5 rounded-lg text-sm font-bold">Jump</button>
            {highlightId && (
              <button data-testid="tree-search-clear" onClick={() => { setTreeQuery(""); setHighlightId(null); }}
                className="px-3 py-2.5 rounded-lg text-sm text-slate-400 border border-amber-500/20 hover:text-amber-300">Clear</button>
            )}
          </div>
          <div className="overflow-x-auto pb-6">
            <div className="min-w-max flex justify-center pt-2">
              {renderNode(tree, "root", 0, expanded, toggle, highlightId)}
            </div>
          </div>
        </div>
      )}

      {/* Direct team */}
      {tab === "direct" && (
        <div className="glass rounded-2xl p-6" data-testid="direct-team-table">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><User className="w-5 h-5 text-amber-400" /> My Direct Team ({directs.length})</h3>
          {directs.length === 0 ? (
            <p className="text-slate-500 text-sm">No direct referrals match. Share your referral link to grow your team.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                  <th className="text-left py-3">Name</th><th className="text-left py-3">Username</th><th className="text-left py-3">Code</th>
                  <th className="text-left py-3">Leg</th><th className="text-right py-3">Business</th><th className="text-center py-3">Rank</th><th className="text-center py-3">Status</th><th className="text-right py-3">Joined</th>
                </tr></thead>
                <tbody>
                  {directs.map((d) => (
                    <tr key={d.id} className="border-b border-slate-800/60 hover:bg-amber-500/5" data-testid={`direct-row-${d.id}`}>
                      <td className="py-3 font-medium">{d.name}</td>
                      <td className="py-3 text-slate-300">{d.username || "—"}</td>
                      <td className="py-3 font-mono-fin text-amber-300">{d.referral_code}</td>
                      <td className="py-3"><SidePill side={d.side} /></td>
                      <td className="py-3 text-right font-mono-fin text-emerald-400">{money(d.business, 0)}</td>
                      <td className="py-3 text-center"><span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400">R{d.rank || 0}</span></td>
                      <td className="py-3 text-center"><StatusBadge active={d.active} /></td>
                      <td className="py-3 text-right text-slate-500 text-xs">{dateFmt(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Full downline */}
      {tab === "downline" && (
        <div className="glass rounded-2xl p-6" data-testid="downline-table">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Network className="w-5 h-5 text-amber-400" /> Full Downline ({members.length})</h3>
          {members.length === 0 ? (
            <p className="text-slate-500 text-sm">No downline members match your search.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                  <th className="text-center py-3">Level</th><th className="text-left py-3">Name</th><th className="text-left py-3">Username</th><th className="text-left py-3">Code</th>
                  <th className="text-left py-3">Sponsor</th><th className="text-left py-3">Leg</th><th className="text-right py-3">Business</th><th className="text-center py-3">Rank</th><th className="text-center py-3">Status</th>
                </tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-slate-800/60 hover:bg-amber-500/5" data-testid={`downline-row-${m.id}`}>
                      <td className="py-3 text-center"><span className="text-xs px-2 py-1 rounded bg-slate-700/40 text-slate-300">L{m.level}</span></td>
                      <td className="py-3 font-medium">{m.name}</td>
                      <td className="py-3 text-slate-300">{m.username || "—"}</td>
                      <td className="py-3 font-mono-fin text-amber-300">{m.referral_code}</td>
                      <td className="py-3 text-slate-400">{m.sponsor_name || "—"}</td>
                      <td className="py-3"><SidePill side={m.side} /></td>
                      <td className="py-3 text-right font-mono-fin text-emerald-400">{money(m.business, 0)}</td>
                      <td className="py-3 text-center"><span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400">R{m.rank || 0}</span></td>
                      <td className="py-3 text-center"><StatusBadge active={m.active} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Level count */}
      {tab === "levels" && (
        <div className="glass rounded-2xl p-6" data-testid="level-count">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Layers className="w-5 h-5 text-amber-400" /> Level Count &amp; Earnings</h3>
          {levelCounts.length === 0 ? (
            <p className="text-slate-500 text-sm">No downline yet. Your team levels will appear here as you grow.</p>
          ) : (
            <div className="space-y-2">
              {levelCounts.map((r) => (
                <div key={r.level} data-testid={`level-row-${r.level}`}
                  className="flex items-center justify-between bg-[#0D0E12] border border-amber-500/15 rounded-xl px-5 py-4">
                  <span className="text-slate-300 font-medium">Level {r.level}</span>
                  <div className="flex items-center gap-6">
                    <span className="font-mono-fin text-amber-300">{r.count} member{r.count === 1 ? "" : "s"}</span>
                    <span className="font-mono-fin font-bold text-emerald-400 w-24 text-right" data-testid={`level-earned-${r.level}`}>{money(r.earned)}</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-5 py-4 mt-2 border-t border-amber-500/15">
                <span className="text-slate-400 uppercase text-xs tracking-wider">Total</span>
                <div className="flex items-center gap-6">
                  <span className="font-mono-fin font-bold text-amber-300">{dl?.total ?? 0} members</span>
                  <span className="font-mono-fin font-bold text-emerald-400 w-24 text-right" data-testid="level-earned-total">{money(totalEarned)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
